import { useQueryParam } from '@hooks/useQueryParams';
import { $api } from '@service/RequestService';
import { isEmptyArr } from '@utils/helper';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

export function useSelectOptions(key: string, id?: string | number | number[]) {
  const {
    i18n: { language }
  } = useTranslation();
  const includeKey = key?.toUpperCase();
  const { generateSearchParam } = useQueryParam();

  if (includeKey?.includes('CURRENCY')) {
    const { data } = useQuery(
      ['constructor-select-currency'],
      async () => {
        const { data } = await $api.get('/v1/currency');

        return data?.data as { id: number; ccy: string }[];
      },
      {
        select: data =>
          data?.map(({ id, ccy }) => ({
            value: id,
            label: ccy
          }))
      }
    );

    return data;
  }

  if (includeKey?.includes('RESPONSIBLE')) {
    const { data } = useQuery(
      ['constructor-select-responsible'],
      async () => {
        const { data } = await $api.get('/v1/contract/employee');

        return data?.data as { id: number; fullName: string }[];
      },
      {
        select: data =>
          data?.map(({ id, fullName }) => ({
            value: id,
            label: fullName
          }))
      }
    );

    return data;
  }

  if (includeKey?.includes('HOUSE')) {
    const { data } = useQuery(
      ['constructor-select-compact'],
      async () => {
        const { data } = await $api.get('/v1/house/compact');

        return data?.data as { id: number; name: string }[];
      },
      {
        select: data =>
          data?.map(({ id, name }) => ({
            value: id,
            label: name
          }))
      }
    );

    return data;
  }

  if (includeKey?.includes('BUILDING')) {
    const { data } = useQuery(
      ['constructor-select-building', id],
      async () => {
        const { data } = await $api.get(`/v1/building/compact?${generateSearchParam({ houseId: id })}`);

        return data?.data as { id: number; name: string }[];
      },
      {
        enabled: !isEmptyArr(id as never),
        select: data =>
          data?.map(({ id, name }) => ({
            value: id,
            label: name
          }))
      }
    );

    return data;
  }

  if (includeKey?.includes('COUNTRY') || includeKey?.includes('CITY')) {
    const { data } = useQuery(
      ['client-filter-params'],
      async () => {
        const { data } = await $api.get('/v1/client/filter/params');

        return data?.data as {
          countries: {
            id: number;
            name: Record<string, string>;
            regions: {
              id: number;
              name: Record<string, string>;
            }[];
          }[];
        };
      },
      {
        select: data => {
          if (id) {
            return data?.countries
              ?.find(({ id: countryId }) => id === countryId)
              ?.regions?.map(({ id, name }) => ({
                value: id,
                label: name?.[language]
              }));
          }

          return data?.countries?.map(({ id, name }) => ({
            value: id,
            label: name?.[language]
          }));
        }
      }
    );

    return data;
  }

  return [];
}
